import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import EulaPopup from '../EulaPopup/EulaPopup';
import { httpPost } from '../../../utils';
import { FEATURE_FLAGS_NAMES, LICENSE_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import useFeature from '../../hooks/featureFlags/useFeature';
import ConfirmationMessage from '../../../../../src/components/Controls/ConfirmationMessage';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';
import Icons from '../../../sf-components/Icon/Icons';
import ColorScheme from '../../../sf-components/ColorScheme/ColorScheme';
import Button from '../../../sf-components/Button/Button';
import ButtonLink from '../../../sf-components/Button/ButtonLink';
import ButtonStyles from '../../../sf-components/Button/ButtonStyles';
import { debounce } from 'lodash';

const LicensePopup = ({ licenseKey, onSave, onCancel, onLicenseUpdate }) => {
    const [inProgress, setInProgress] = useState(false);
    const systemMode = useFeature(FEATURE_FLAGS_NAMES.SystemMode);
    const [enteredLicenseKey, setEnteredLicenseKey] = useState(licenseKey);
    const [licenseData, setLicenseData] = useState(null);
    const [showEula, setShowEula] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [isUserConfirmed, setIsUserConfirmed] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState(null);
    const [error, setError] = useState(null);
    const [isOnDrag, setIsOnDrag] = useState(false);
    const [isInputChanged, setIsInputChanged] = useState(false);

    const licenseKeyInputChanged = (event) => {
        setIsInputChanged(true);
        setEnteredLicenseKey(event.target.value.trim());
    };

    const checkLicenseKey = useMemo(() => {
        return debounce(async (enteredLicenseKey) => {
            try {
                const { data } = await httpPost('/Settings/CheckLicenseKey', { licenseKey: enteredLicenseKey });
                setError(null);
                setLicenseData(data);

                const licenseDetails = {
                    endDate: data.endDate,
                    licenseKey: enteredLicenseKey,
                    numOfMonitoredUsers: data.numOfMonitoredUsers,
                    numOfProtectedUsers: data.numOfProtectedUsers,
                    licenseData: data.licenseData,
                    companyName: data.companyName
                };
                await onLicenseUpdate(
                    licenseDetails,
                    data.towerSenderConnectionStrings,
                    data.towerReceiverConnectionStrings
                );
                setInProgress(false);
            } catch (err) {
                setError(err.response && err.response.data ? err.response.data.error : err.message);
                setInProgress(false);
            }
        }, 500);
    }, [onLicenseUpdate]);

    useEffect(() => {
        if (checkLicenseKey && isInputChanged) {
            setInProgress(true);
            checkLicenseKey(enteredLicenseKey);
        }
    }, [enteredLicenseKey, checkLicenseKey, isInputChanged]);

    const validateLicenseKey = useCallback(async () => {
        try {
            await httpPost('/Settings/ValidateLicenseKey', { licenseKey: enteredLicenseKey });
            setError(null);
            return true;
        } catch (err) {
            setError(err.response && err.response.data ? err.response.data.error : err.message);
            return false;
        }
    }, [enteredLicenseKey]);

    const readFileContentHandler = (file) => {
        setError(null);
        if (file) {
            const reader = new FileReader();
            reader.readAsText(file, 'UTF-8');
            reader.onload = function (event) {
                setIsInputChanged(true);
                setEnteredLicenseKey(event.target.result.trim());
            };
            reader.onerror = function () {
                setError('Error reading file. please try again');
            };
        }
    };

    const onConfirmation = () => {
        setIsUserConfirmed(true);
        setShowConfirmationModal(false);
    };

    const buildConfirmationMessage = (configuredRavenData, enteredRavenData) => {
        if (!enteredRavenData || !configuredRavenData) {
            return null;
        }
        const isEntityPathEqual = configuredRavenData.entityPath === enteredRavenData.entityPath;
        const isConnectionStringsEqual = configuredRavenData.endpoint === enteredRavenData.endpoint;

        if (!isEntityPathEqual && !isConnectionStringsEqual) {
            return `The license Email Domain "${
                configuredRavenData.entityPath || 'N/A'
            }" and Cloud Messaging Services "${
                configuredRavenData.endpoint || 'N/A'
            }" that was configured in the outdated license does not match the current Email Domain "${
                enteredRavenData.entityPath || 'N/A'
            }" and Cloud Messaging Services "${enteredRavenData.endpoint || 'N/A'}".`;
        } else if (!isEntityPathEqual) {
            return `The license Email Domain "${
                configuredRavenData.entityPath || 'N/A'
            }" that was configured in the outdated license does not match the current Email Domain "${
                enteredRavenData.entityPath || 'N/A'
            }".`;
        } else if (!isConnectionStringsEqual) {
            return `The license Cloud Messaging Services "${
                configuredRavenData.endpoint || 'N/A'
            }" that was configured in the outdated license does not match the current Cloud Messaging Services "${
                enteredRavenData.endpoint || 'N/A'
            }".`;
        }
    };

    const saveHandler = async (eulaAgreed) => {
        setShowEula(false);
        setShowConfirmationModal(false);
        setInProgress(true);
        setError(null);

        try {
            const { configuredRavenData, enteredRavenData, type } = licenseData;

            const isEndpointChanged =
                configuredRavenData && enteredRavenData && configuredRavenData.endpoint !== enteredRavenData.endpoint;
            const isEntityPathChanged =
                configuredRavenData &&
                enteredRavenData &&
                configuredRavenData.entityPath !== enteredRavenData.entityPath;
            if (
                type !== LICENSE_TYPES.LICENSE_SERVER &&
                (isEndpointChanged || isEntityPathChanged) &&
                !isUserConfirmed &&
                enteredRavenData &&
                configuredRavenData
            ) {
                setShowConfirmationModal(true);
                setInProgress(false);
                setConfirmationMessage(buildConfirmationMessage(configuredRavenData, enteredRavenData));
            }

            const isActivationKeyValid = !!licenseData.licenseData.licenseId || (await validateLicenseKey());

            if (!isActivationKeyValid) {
                setInProgress(false);
            } else if (!eulaAgreed && systemMode === SYSTEM_MODES.ON_PREM) {
                setShowEula(true);
                setInProgress(false);
            } else {
                const res = await onSave();

                if (res.success) {
                    setIsInputChanged(false);
                } else {
                    setError(res.errorMessage);
                }
            }
        } catch (err) {
            setError(err.response && err.response.data ? err.response.data.error : err.message);
            setInProgress(false);
        }
    };

    return (
        <>
            <Modal
                className="z-[892]"
                backdropClassName={'z-[890]'}
                dialogClassName="modal-md"
                id="settings-modal"
                show
                onHide={onCancel}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <div className="text-sf font-sf font-medium text-sf-gray-800">Update License Key</div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="w-full flex-col justify-start items-start gap-2 inline-flex">
                        <div className="w-full flex-col justify-center items-start gap-2 inline-flex">
                            <div className="justify-start items-center gap-1 inline-flex">
                                <div className="text-sf-gray-800 font-medium text-sf font-sf">License key</div>
                            </div>
                            <div className="w-full h-40 self-stretch grow px-3 py-2 rounded-lg border border-gray-200 justify-start items-start gap-2 inline-flex">
                                <textarea
                                    id="licenseKeyInput"
                                    placeholder="Enter your license key"
                                    className="w-full flex border-none self-stretch text-sf-gray-800 font-normal font-sf leading-tight"
                                    value={enteredLicenseKey}
                                    onChange={licenseKeyInputChanged}></textarea>
                            </div>
                        </div>
                        <label
                            className={`w-full flex bg-sf-gray-50 gap-2 h-16 p-4 rounded-lg border border-sf-gray-200 border-dashed justify-start items-center inline-flex ${
                                isOnDrag ? 'border-sf-primary-300' : 'border-sf-gray-200'
                            }`}
                            htmlFor="dropzone-file"
                            onDragEnter={(e) => {
                                e.preventDefault();
                                setIsOnDrag(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                setIsOnDrag(false);
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsOnDrag(true);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                readFileContentHandler(e.dataTransfer.files[0]);
                            }}>
                            <div className="w-10 h-10 bg-sf-white rounded-lg justify-center items-center gap-2 flex">
                                <Icons.CloudUpload fill={ColorScheme['sf-gray-500']} />
                            </div>
                            <div className="flex-col justify-center items-start inline-flex">
                                <div className="justify-center items-center gap-1 inline-flex">
                                    <div className="text-center text-sf-gray-800 font-medium text-xs">
                                        Drop new license file here or
                                    </div>
                                    <ButtonLink text={'Browse'} className="text-sf" />
                                </div>
                                <div className="text-center text-sf-gray-500 text-xs font-normal">
                                    Max file size up to 10 MB
                                </div>
                            </div>
                            <input
                                id="dropzone-file"
                                type="file"
                                className="hidden"
                                onChange={(e) => readFileContentHandler(e.target.files[0])}
                            />
                        </label>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className="flex justify-end items-center gap-2">
                        <Button buttonStyle={ButtonStyles.TertiarySolid} text="Cancel" onClick={onCancel} />
                        <Button
                            disabled={inProgress || error || !isInputChanged}
                            text="Save Changes"
                            onClick={() => saveHandler(licenseData.eulaAgreed)}
                        />
                    </div>
                    {error && <div className="footer-error">* {error}</div>}
                </Modal.Footer>
            </Modal>
            {showEula && <EulaPopup onAgree={saveHandler} onDisagree={() => setShowEula(false)} />}
            {showConfirmationModal && (
                <ConfirmationMessage
                    title="Are you sure you want to proceed?"
                    body={confirmationMessage}
                    approveText="proceed"
                    OnCancel={() => setShowConfirmationModal(false)}
                    OnApprove={onConfirmation}
                />
            )}
        </>
    );
};

export default LicensePopup;
